import { create } from 'zustand';
import { getApiUrl } from "@/utils/apiConfig";

const API_URL = getApiUrl();

export interface EventMetrics {
  eventId: string;
  like: number;
}

interface EventMetricsStore {
  metrics: Record<string, number>;
  views: Record<string, number>;
  userLikes: Record<string, boolean>; // eventId -> true if liked by current user/client
  clientId: string;
  fetchMetrics: () => Promise<void>;
  toggleLike: (eventId: string, entityType?: string) => Promise<void>;
  trackView: (eventId: string, entityType?: string) => Promise<void>;
  getOrCreateClientId: () => string;
}

export const useEventMetricsStore = create<EventMetricsStore>((set, get) => ({
  metrics: {},
  views: {},
  userLikes: {},
  clientId: '',
  
  getOrCreateClientId: () => {
    let id = localStorage.getItem("kaynin_client_id");
    if (!id) {
      id = 'client_' + Math.random().toString(36).substr(2, 9) + Date.now();
      localStorage.setItem("kaynin_client_id", id);
    }
    return id;
  },

  fetchMetrics: async () => {
    try {
      // Fetch likes and views together
      const res = await fetch(`${API_URL}/likes.php?action=get_views`);
      if (res.ok) {
        const data = await res.json();
        if (data.likes && data.views) {
          set({ metrics: data.likes, views: data.views });
        } else {
          set({ metrics: data });
        }
      }
      
      // We should also initialize userLikes from local storage as a fallback, 
      // but ideally this is handled per component for UI feedback.
      const localLikesStr = localStorage.getItem("kaynin_user_likes");
      if (localLikesStr) {
        set({ userLikes: JSON.parse(localLikesStr) });
      }
      
      const clientId = get().getOrCreateClientId();
      if (!get().clientId) {
        set({ clientId });
      }
    } catch (e) {
      console.error("Failed to fetch event metrics", e);
    }
  },

  toggleLike: async (eventId: string, entityType = 'event') => {
    const { metrics, userLikes, getOrCreateClientId } = get();
    const clientId = getOrCreateClientId();
    const isCurrentlyLiked = !!userLikes[eventId];
    
    // Optimistic UI update
    const newLikes = { ...userLikes, [eventId]: !isCurrentlyLiked };
    const newMetrics = { ...metrics };
    
    if (isCurrentlyLiked) {
        newMetrics[eventId] = Math.max(0, (newMetrics[eventId] || 1) - 1);
    } else {
        newMetrics[eventId] = (newMetrics[eventId] || 0) + 1;
    }
    
    set({ userLikes: newLikes, metrics: newMetrics });
    localStorage.setItem("kaynin_user_likes", JSON.stringify(newLikes));

    // Send to backend
    try {
      await fetch(`${API_URL}/likes.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_id: eventId,
          client_id: clientId,
          entity_type: entityType,
          action: isCurrentlyLiked ? 'remove' : 'add'
        })
      });
    } catch (e) {
      console.error("Failed to toggle like", e);
      // We could revert optimistic update here if needed
    }
  },

  trackView: async (eventId: string, entityType = 'emission_view') => {
    const { getOrCreateClientId } = get();
    const clientId = getOrCreateClientId();

    // Send to backend (it will ignore if already tracked due to UNIQUE KEY)
    try {
      await fetch(`${API_URL}/likes.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_id: eventId,
          client_id: clientId,
          entity_type: entityType,
          action: 'add'
        })
      });
    } catch (e) {
      console.error("Failed to track view", e);
    }
  }
}));

// Fallback functions to not break existing code before full refactoring
export function incrementEventMetric(eventId: string) {
    useEventMetricsStore.getState().toggleLike(eventId, 'event');
}

export function decrementEventMetric(eventId: string) {
    useEventMetricsStore.getState().toggleLike(eventId, 'event');
}

export function getEventMetrics(): Record<string, EventMetrics> {
    const metrics = useEventMetricsStore.getState().metrics;
    const result: Record<string, EventMetrics> = {};
    Object.keys(metrics).forEach(key => {
        result[key] = { eventId: key, like: metrics[key] };
    });
    return result;
}
